import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Select } from 'antd';
import { useSearchContacts } from '../hooks/useSearchContacts';
import { useDebounce } from '../../../hooks/useDebounce';
import { getContactById } from '../api/contacts';
import { contactDisplayName } from '../contactType';

const EMPTY_ARRAY: string[] = [];

export interface ContactSearchSelectProps {
  value?: string[];
  onChange?: (value: string[]) => void;
  style?: React.CSSProperties;
  placeholder?: string;
}

const ContactSearchSelect: React.FC<ContactSearchSelectProps> = ({
  value = EMPTY_ARRAY,
  onChange,
  style,
  placeholder = 'Search contacts...',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 300);
  const { data: results = [], isFetching } = useSearchContacts(debouncedSearch);
  const labelCache = useRef<Map<string, string>>(new Map());
  const prevSelectedRef = useRef(value);

  // Local state buffer — updated immediately on user interaction (tag add/remove)
  // then synced from props when external state (URL) settles.
  // This avoids Ant Design's Select waiting for a URL roundtrip to reflect tag removal.
  const [selected, setSelected] = useState<string[]>(value);
  useEffect(() => {
    setSelected(value);
  }, [value]);

  // Manage label cache in effect (not in render, to comply with React 19 lint rules)
  useEffect(() => {
    // Prune cache for removed items
    if (prevSelectedRef.current !== selected) {
      const prev = prevSelectedRef.current;
      const removed = prev.filter((id) => !selected.includes(id));
      for (const id of removed) {
        labelCache.current.delete(id);
      }
      prevSelectedRef.current = selected;
    }

    // Cache labels from search results
    for (const r of results) {
      if (selected.includes(r.id) && !labelCache.current.has(r.id)) {
        labelCache.current.set(r.id, contactDisplayName(r));
      }
    }

    // Fetch labels for selected items not yet in cache
    const toFetch = selected.filter((id) => !labelCache.current.has(id));
    if (toFetch.length === 0) return;
    let stale = false;
    Promise.allSettled(toFetch.map((id) => getContactById(id))).then((settled) => {
      if (stale) return;
      for (let i = 0; i < settled.length; i++) {
        const r = settled[i];
        if (r.status === 'fulfilled') {
          const c = r.value;
          labelCache.current.set(toFetch[i], contactDisplayName(c));
        }
      }
    });
    return () => {
      stale = true;
    };
  }, [selected, results]);

  const options = useMemo(() => {
    const list = results.map((r) => ({
      value: r.id,
      label: contactDisplayName(r),
    }));
    const existingIds = new Set(list.map((o) => o.value));
    for (const id of selected) {
      if (!existingIds.has(id)) {
        list.unshift({
          value: id,
          label: labelCache.current.get(id) ?? id,
        });
      }
    }
    return list;
  }, [results, selected]);

  const handleChange = useCallback(
    (v: string[]) => {
      setSelected(v);
      onChange?.(v);
    },
    [onChange],
  );

  const handleSearch = useCallback((v: string) => {
    setSearchTerm(v);
  }, []);

  return (
    <Select<string[]>
      mode="multiple"
      showSearch
      filterOption={false}
      loading={isFetching}
      value={selected}
      onChange={handleChange}
      onSearch={handleSearch}
      options={options}
      placeholder={placeholder}
      style={style}
      maxTagCount="responsive"
      notFoundContent={
        searchTerm.trim().length === 0
          ? 'Type to search contacts'
          : null
      }
    />
  );
};

export default ContactSearchSelect;
